import { useFlags } from "launchdarkly-react-client-sdk";
import * as React from "react";
import {
  startTransition,
  useCallback,
  useEffect,
  useMemo,
  useState
} from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import Grid from "@mui/material/Grid";
import useTheme from "@mui/material/styles/useTheme";
import useMediaQuery from "@mui/material/useMediaQuery";

import { vestResolver } from "@hookform/resolvers/vest";
import { add, addMinutes } from "date-fns";
import { merge } from "lodash";

import { useAppDispatch, useAppSelector } from "app/hooks";
import { misc } from "app/themes/colors";
import {
  resetConfirmDialog,
  selectConfirmDialogTitle
} from "components/confirm-dialog/confirm-dialog.slice";
import ModalForm from "components/modal-form";
import {
  APPOINTMENT_STATUS_CANCELED,
  APPOINTMENT_STATUS_COMPLETED,
  APPOINTMENT_STATUS_DRAFT,
  APPOINTMENT_STATUS_NO_SHOW,
  APPOINTMENT_STATUS_UNCONFIRMED,
  EVENT_TYPES,
  EVENT_TYPE_APPOINTMENT,
  EVENT_TYPE_APPOINTMENT_BLOCK,
  EVENT_TYPE_CLINIC_EVENT,
  EVENT_TYPE_PERSONAL_EVENT,
  EVENT_TYPE_PERSONAL_WORKSHIFT,
  NOTIFICATION_METHOD_EMAIL,
  NOTIFICATION_METHOD_SMS,
  REOCCURRENCE_PERIOD_TYPE_DAY_IN_WEEK_OF_MONTH,
  REOCCURRENCE_PERIOD_TYPE_MONTHLY,
  REOCCURRENCE_PERIOD_TYPE_WEEKLY
} from "constants/appointments";
import {
  APPOINTMENT_EMAIL_NOTIFICATION_FEATURE,
  ASSIGNED_APPOINTMENT_TAGS_FEATURE,
  CLINIC_EVENTS_FEATURE,
  MANDATORY_REFERRAL_SOURCE_ON_APPOINTMENT_MODAL,
  RECURRING_APPOINTMENTS_FEATURE,
  SCHEDULE_VIEW_IN_PATIENT_HEADER,
  TAG_TRANSFER_ON_RESCHEDULE_FEATURE
} from "constants/feature-flags";
import {
  COMMUNICATION_METHOD_DO_NOT_CONTACT,
  COMMUNICATION_METHOD_EMAIL,
  COMMUNICATION_METHOD_TEXT_SMS,
  PHONE_TYPE_MOBILE
} from "constants/patients";
import { SUPADEMO_APPOINTMENT_FORM_DEMO_ID } from "constants/supa-demo";
import {
  selectCurrentClinic,
  selectCurrentPractice,
  selectCurrentRegion,
  selectCurrentUser,
  selectNumberOfPracticeClinics
} from "features/auth/auth-slice";
import usePatientCreateAppointmentMode from "features/scheduling/patient-create-appointment-mode-modal/use-patient-create-appointment-mode.hook";
import {
  closeForm,
  endFollowUpScheduling,
  endPatientCreateAppointmentMode,
  removeCarriedReschedulingTag,
  selectFollowUpSchedulingValues,
  selectFormOpen,
  selectOpenFormId,
  selectPatientCreateAppointmentModeValues,
  selectReschedulingCarriedTags,
  selectReschedulingValues
} from "features/scheduling/scheduler-slice";
import { ErrorsState } from "hooks/use-errors.hook";
import { appointmentsApi } from "services/appointments";
import { useGetAppointmentAssignedTagsQuery } from "services/assigned-tags";
import { type AppointmentType } from "services/types/appointment-types.type";
import {
  type Appointment,
  type AppointmentAssignedTagsInput,
  type WebPatientData
} from "services/types/appointments.type";
import { type AssignedTag } from "services/types/assigned-tag.type";
import { type Clinic } from "services/types/clinics.type";
import { type AppointmentBlock } from "services/types/events.type";
import { IntakeFormListId } from "services/types/onboarding-request.type";
import { Physician } from "services/types/physician.type";
import {
  ReferralSource,
  SubReferralSource
} from "services/types/referral-source.type";
import { type Room } from "services/types/rooms.type";
import { type Schedule } from "services/types/schedules.type";
import { type MinimalStaffMember } from "services/types/staff.type";
import { type Tag } from "services/types/tags.type";
import {
  convertDateFromCurrentTimezoneToTimezone,
  getDateTime,
  parseBackendDateOnly,
  strToDuration
} from "utils/common";

import { buildReschedTransferCreates } from "../rescheduling-modal/reschedule-tags.util";
import { getReschedulingTitle } from "../rescheduling-modal/rescheduling.util";
import {
  CONFIRMATION_TITLE_APPOINTMENT_OVERLAPS,
  CONFIRMATION_TITLE_OUTSIDE_WORKING_HOURS
} from "../scheduler/scheduler.constants";
import useReservedAppointmentTypes from "../scheduler/use-reserved-appointment-types.hook";
import {
  appointmentFormValidator,
  formatAppointment,
  formatAppointmentBlock,
  formatPersonalEvent,
  getAssignedTagChanges
} from "./appointment-form.util";
import EventTypeField from "./fields/event-type-field";
import AppointmentBlockFormView from "./form-views/appointment-block-form-view.component";
import AppointmentFormView from "./form-views/appointment-form-view.component";
import EventFormView from "./form-views/event-form-view.component";
import StaffAvailability from "./staff-availability";
import { ShowPickersProvider } from "./use-show-pickers.hook";

type AppointmentFormProps = {
  onCancel: () => void;
  onSave: (appointmentData: Partial<Appointment>) => Promise<{
    isSuccess: boolean;
    result: Appointment;
  }>;
  currentAppointment: Partial<AppointmentFormValues>;
  errors: ErrorsState;
  formId: string;
  isRescheduling?: boolean;
};

export type AppointmentFormValues = {
  id?: number | null;
  modelId?: number | null;
  title: string;
  event_type: string;
  is_recurring: boolean;
  recurrence_period?: string | null;
  recurrence_day?: number | null;
  recurrence_week?: number | null;
  clinic: Clinic | null;
  appointment_type: AppointmentType | null;
  companion_type: number | "";
  companion_name: string;
  companion_present?: boolean;
  event_end_date: Date | null;
  status?: number;
  allow_overlap: boolean;
  allow_overlapping_blocks?: boolean;
  show_room_warning: boolean;
  allow_room_conflict: boolean;
  appointment_date: Date;
  start_time: Date;
  end_time: Date;
  patient: any | null;
  staff_member: MinimalStaffMember | null;
  staff_members: MinimalStaffMember[];
  removed_staff_members?: MinimalStaffMember[];
  room: Room | null;
  referral_source: number | ReferralSource | "" | null;
  sub_referral_source: number | SubReferralSource | string | "" | null;
  sub_referral_source_object?: any;
  sub_referral_source_required?: boolean;
  referral: number;
  should_notify: boolean;
  should_create_schedule_for_staff: boolean;
  is_global_practice_event: boolean;
  overwrite_block?: boolean;
  notes: string;
  web_patient: WebPatientData | null;
  referring_physician: Partial<Physician> | null;
  notification_method: number | null;
  clinicIdToOverride?: number | null;
  should_create_intake_form_request: boolean;
  intake_form_request: {
    notification_method: number | null;
    intake_form_request_submissions: {
      create: IntakeFormListId[];
    };
  };
  timezone?: string;
  rescheduled_appointment_id?: number;
  schedule?: Schedule;
  rrule?: string | null;
  assigned_tags?: AssignedTag[];
  supervising_provider?: MinimalStaffMember | null;
  is_direct_supervisor?: boolean;
};

export const APPOINTMENT_FORM_DEFAULT_VALUES: AppointmentFormValues = {
  id: null,
  modelId: null,
  title: "",
  is_recurring: false,
  clinic: null,
  event_type: EVENT_TYPE_APPOINTMENT,
  recurrence_period: REOCCURRENCE_PERIOD_TYPE_WEEKLY,
  recurrence_day: null,
  recurrence_week: null,
  appointment_type: null,
  companion_type: "",
  companion_name: "",
  event_end_date: null,
  appointment_date: new Date(),
  allow_overlap: false,
  allow_overlapping_blocks: false,
  show_room_warning: false,
  allow_room_conflict: false,
  start_time: new Date(),
  end_time: addMinutes(new Date(), 60),
  staff_member: null,
  staff_members: [],
  room: null,
  patient: null,
  referral: 1,
  referral_source: "",
  sub_referral_source: "",
  sub_referral_source_object: null,
  sub_referral_source_required: false,
  should_notify: true,
  should_create_schedule_for_staff: false,
  is_global_practice_event: false,
  overwrite_block: undefined,
  notes: "",
  web_patient: null,
  referring_physician: null,
  notification_method: NOTIFICATION_METHOD_SMS,
  clinicIdToOverride: null,
  should_create_intake_form_request: false,
  intake_form_request: {
    intake_form_request_submissions: {
      create: []
    },
    notification_method: null
  },
  supervising_provider: null,
  is_direct_supervisor: false
};

export default function AppointmentForm({
  onCancel,
  onSave,
  currentAppointment,
  errors,
  formId = "appointment",
  isRescheduling
}: AppointmentFormProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("mobile"));
  const dispatch = useAppDispatch();
  const flags = useFlags();
  const recurringAppointmentFeatureEnabled =
    flags[RECURRING_APPOINTMENTS_FEATURE];

  const [isSubmitting, setIsSubmitting] = useState(false);

  const isEmailNotificationEnabled =
    flags[APPOINTMENT_EMAIL_NOTIFICATION_FEATURE];
  const isClinicEventsEnabled = flags[CLINIC_EVENTS_FEATURE];

  const isAssignedAppointmentTagsFeature =
    flags[ASSIGNED_APPOINTMENT_TAGS_FEATURE];

  const isScheduleViewInPatientHeaderFeature =
    flags[SCHEDULE_VIEW_IN_PATIENT_HEADER];

  const isTagTransferOnRescheduleFeature =
    flags[TAG_TRANSFER_ON_RESCHEDULE_FEATURE];

  const { originalAppointmentData, rescheduledAppointmentData } =
    useAppSelector(selectReschedulingValues);
  const carriedReschedulingTags = useAppSelector(selectReschedulingCarriedTags);
  // Reschedule POSTs a NEW appointment and the curated carried-tag set is the
  // single source of truth for what transfers. During a transfer the form must
  // mirror that set, never the source appointment's server-side tags.
  const isReschedTransfer =
    isTagTransferOnRescheduleFeature && Boolean(originalAppointmentData);
  const followUpScheduling = useAppSelector(selectFollowUpSchedulingValues);
  const openFormId = useAppSelector(selectOpenFormId);

  const currentClinic = useAppSelector(selectCurrentClinic);
  const currentRegion = useAppSelector(selectCurrentRegion);
  const schedulingStaffIsAvailability =
    currentClinic?.scheduling_staff_availability;

  const currentPractice = useAppSelector(selectCurrentPractice);
  const user = useAppSelector(selectCurrentUser);
  const isOpen = useAppSelector(selectFormOpen);
  const numberOfPracticeClinics =
    useAppSelector(selectNumberOfPracticeClinics) || 0;
  const multiClinic = numberOfPracticeClinics > 1;

  const changeTitle = useAppSelector(selectConfirmDialogTitle);
  const practiceAllowOverlap = Boolean(
    currentPractice?.scheduling_allow_overlap
  );

  const isAppointmentInFinalState = [
    APPOINTMENT_STATUS_NO_SHOW,
    APPOINTMENT_STATUS_COMPLETED,
    APPOINTMENT_STATUS_CANCELED
  ].includes(Number(currentAppointment?.status));

  const defaultValues = useMemo(() => {
    const isEveryDayInAWeekOfTheMonth =
      currentAppointment.recurrence_period ===
        REOCCURRENCE_PERIOD_TYPE_MONTHLY &&
      Boolean(currentAppointment.recurrence_week);

    const recurrenceData: any =
      recurringAppointmentFeatureEnabled &&
      currentAppointment.event_type === EVENT_TYPE_APPOINTMENT
        ? {
            recurrence_period: isEveryDayInAWeekOfTheMonth
              ? REOCCURRENCE_PERIOD_TYPE_DAY_IN_WEEK_OF_MONTH
              : (currentAppointment.recurrence_period ??
                currentAppointment?.schedule?.recurrence_period),
            recurrence_day: currentAppointment?.schedule?.recurrence_day,
            recurrence_week: currentAppointment?.schedule?.recurrence_week,
            is_recurring: Boolean(
              currentAppointment?.schedule?.recurrence_period
            ),
            event_end_date: currentAppointment?.schedule?.end_date
              ? parseBackendDateOnly(currentAppointment?.schedule?.end_date)
              : undefined
          }
        : {
            recurrence_period: isEveryDayInAWeekOfTheMonth
              ? REOCCURRENCE_PERIOD_TYPE_DAY_IN_WEEK_OF_MONTH
              : currentAppointment.recurrence_period
          };
    const rrule =
      currentAppointment?.rrule || currentAppointment?.schedule?.rrule;

    if (rrule) {
      recurrenceData.is_recurring = true;
      recurrenceData.schedule = {
        rrule
      };
    }

    // Follow-up scheduling data (pre-fill patient and appointment type)
    const followUpData = followUpScheduling.active
      ? {
          patient: followUpScheduling.patient,
          appointment_type: followUpScheduling.appointmentType
        }
      : {};

    const merged = merge(
      {},
      APPOINTMENT_FORM_DEFAULT_VALUES,
      {
        should_notify: Boolean(
          currentPractice?.enable_appointment_communications ?? true
        )
      },
      currentAppointment,
      recurrenceData,
      {
        staffMember: rescheduledAppointmentData?.staffMember,
        staffMemberId: rescheduledAppointmentData?.staffMemberId
      },
      followUpData
    );

    // When opening for follow-up scheduling, use the appointment type's
    // default_duration so the new appointment has the correct duration
    // (not the slot's cell duration, e.g. 30 min).
    if (
      followUpScheduling.active &&
      followUpScheduling.appointmentType?.default_duration
    ) {
      const start = merged.start_time ?? merged.appointment_date ?? new Date();
      const startDate =
        start instanceof Date ? start : new Date(start as unknown as string);
      merged.end_time = add(
        startDate,
        strToDuration(followUpScheduling.appointmentType.default_duration)
      );
    }

    return merged;
  }, [
    currentAppointment,
    recurringAppointmentFeatureEnabled,
    rescheduledAppointmentData?.staffMember,
    rescheduledAppointmentData?.staffMemberId,
    currentPractice?.enable_appointment_communications,
    followUpScheduling.active,
    followUpScheduling.patient,
    followUpScheduling.appointmentType
  ]);

  const isReferralSourceRequired =
    flags[MANDATORY_REFERRAL_SOURCE_ON_APPOINTMENT_MODAL];
  const isSubReferralSourceRequired = Boolean(
    currentPractice?.appointment_settings?.required_fields?.sub_referral_source
  );

  const formMethods = useForm<AppointmentFormValues>({
    mode: "onChange",
    resolver: vestResolver(
      appointmentFormValidator({
        isReferralSourceRequired,
        isSubReferralSourceRequired
      }),
      undefined,
      {
        mode: "sync"
      }
    ),
    defaultValues
  });
  const {
    handleSubmit,
    control,
    reset: resetFormValues,
    setValue,
    watch,
    clearErrors,
    getValues
  } = formMethods;

  const [
    eventType,
    eventId,
    overwriteBlock,
    patient,
    appointmentType,
    clinic,
    isGlobalPracticeEvent,
    should_create_intake_form_request,
    is_recurring,
    staffMember,
    allowOverlappingBlocks
  ]: [
    string,
    number | null,
    boolean | undefined,
    any,
    AppointmentType | null,
    Clinic | null,
    boolean,
    boolean,
    boolean,
    MinimalStaffMember | null,
    boolean
  ] = watch([
    "event_type",
    "id",
    "overwrite_block",
    "patient",
    "appointment_type",
    "clinic",
    "is_global_practice_event",
    "should_create_intake_form_request",
    "is_recurring",
    "staff_member",
    "allow_overlapping_blocks"
  ] as const) as [
    string,
    number | null,
    boolean | undefined,
    any,
    AppointmentType | null,
    Clinic | null,
    boolean,
    boolean,
    boolean,
    MinimalStaffMember | null,
    boolean
  ];
  const availableStaffClinics = useMemo(() => {
    const availableRegionClinics = currentRegion?.clinics || [];
    if (!staffMember) {
      return [];
    }
    const staffClinicsId = new Set(
      (staffMember?.clinics || []).map(({ id }: { id: number }) => id)
    );
    return availableRegionClinics.filter(({ id }: { id: number }) =>
      staffClinicsId.has(id)
    );
  }, [staffMember, currentRegion]);

  useEffect(() => {
    clearErrors();
  }, [eventType, clearErrors]);

  // Seed the staff field when the user flips between Personal and Clinic while editing.
  // serverEventTypeRef records the type as it came from the server so that a programmatic
  // form reset to the server state (same eventId, same original type) is not mistaken for
  // a user-initiated flip.
  const serverEventTypeRef = React.useRef<string | null>(null);
  const prevTypeFlipStateRef = React.useRef<{
    eventId: number | null;
    eventType: string | null;
  }>({ eventId: null, eventType: null });

  useEffect(() => {
    const prev = prevTypeFlipStateRef.current;
    prevTypeFlipStateRef.current = { eventId, eventType };

    if (prev.eventId !== eventId) {
      // Different event loaded (form reset) — update the server baseline and bail.
      serverEventTypeRef.current = eventType;
      return;
    }
    // No actual type change or no prior state.
    if (prev.eventType === null || prev.eventType === eventType) return;
    // Only relevant while editing an existing block event.
    if (!eventId) return;
    // If the type is reverting to what the server returned, this is a programmatic
    // reset (e.g. RHF reset() after a failed save) — not a user-initiated flip.
    if (eventType === serverEventTypeRef.current) return;

    if (
      prev.eventType === EVENT_TYPE_PERSONAL_EVENT &&
      eventType === EVENT_TYPE_CLINIC_EVENT
    ) {
      // Seed the Clinic M2M list with the Personal Event's single staff member so the
      // assignee is not silently lost on conversion (B1).
      const currentStaffMember = getValues("staff_member");
      setValue("staff_member", null);
      if (currentStaffMember) {
        setValue("staff_members", [currentStaffMember]);
      }
    } else if (
      prev.eventType === EVENT_TYPE_CLINIC_EVENT &&
      eventType === EVENT_TYPE_PERSONAL_EVENT
    ) {
      // Seed staff_member from the Clinic M2M; leave staff_members intact so
      // formatPersonalEvent can build the M2M remove list on save.
      const staffMembers = getValues("staff_members");
      setValue("staff_member", staffMembers?.[0] ?? user ?? null);
    }
  }, [eventType, eventId, getValues, setValue, user]);

  useEffect(() => {
    clearErrors("intake_form_request.intake_form_request_submissions.create");
  }, [should_create_intake_form_request, clearErrors]);

  useEffect(() => {
    clearErrors("recurrence_day");
  }, [is_recurring, clearErrors]);

  const isAppointmentConfirmed =
    eventType === EVENT_TYPE_APPOINTMENT &&
    ![APPOINTMENT_STATUS_DRAFT, APPOINTMENT_STATUS_UNCONFIRMED].includes(
      Number(currentAppointment?.status)
    );

  const { isReserved, intersectingBlocks } = useReservedAppointmentTypes();

  const [isTagsInitialized, setIsTagsInitialized] = useState<boolean>();
  const [assignedTags, setAssignedTags] = useState<
    Array<AssignedTag & { isDirty?: boolean }>
  >([]);

  const reset = useCallback(
    (params?: any) => {
      resetFormValues(params);
      setAssignedTags([]);
      setIsTagsInitialized(false);
    },
    [resetFormValues]
  );

  useEffect(() => {
    startTransition(() => {
      reset(
        merge(defaultValues, {
          should_create_schedule_for_staff:
            changeTitle === CONFIRMATION_TITLE_OUTSIDE_WORKING_HOURS
              ? true
              : undefined,
          allow_overlap:
            changeTitle === CONFIRMATION_TITLE_APPOINTMENT_OVERLAPS ||
            practiceAllowOverlap
              ? true
              : undefined
        })
      );
    });
  }, [defaultValues, changeTitle, reset, practiceAllowOverlap]);

  // Reset tag state every time the form opens so back-navigation always shows
  // fresh server data, even when the component stays mounted (no remount).
  useEffect(() => {
    if (isOpen && openFormId === formId) {
      setAssignedTags([]);
      setIsTagsInitialized(false);
    }
  }, [isOpen, openFormId, formId]);

  const submitIsDisabled = useMemo(() => {
    if (
      isReserved &&
      !overwriteBlock &&
      eventType !== EVENT_TYPE_APPOINTMENT_BLOCK
    ) {
      return true;
    }

    return Boolean(
      eventType === EVENT_TYPE_APPOINTMENT_BLOCK &&
        intersectingBlocks?.length &&
        intersectingBlocks.every(
          (appointment: AppointmentBlock) => appointment.id !== eventId
        ) &&
        !allowOverlappingBlocks
    );
  }, [
    eventId,
    eventType,
    intersectingBlocks,
    isReserved,
    overwriteBlock,
    allowOverlappingBlocks
  ]);

  const shouldNotify = useMemo(
    () =>
      Boolean(
        currentPractice?.enable_appointment_communications &&
          Number(patient?.preferred_communication_method) !==
            COMMUNICATION_METHOD_DO_NOT_CONTACT &&
          Boolean(appointmentType?.patient_notifications_enabled)
      ),
    [
      appointmentType?.patient_notifications_enabled,
      currentPractice?.enable_appointment_communications,
      patient?.preferred_communication_method
    ]
  );

  useEffect(() => {
    if (!eventId && !isRescheduling) {
      if (isEmailNotificationEnabled) {
        const hasPhone =
          Boolean(patient?.phone) &&
          (patient?.phone_type ?? PHONE_TYPE_MOBILE) === PHONE_TYPE_MOBILE;

        const hasEmail = Boolean(patient?.email);
        const doNotEmail = Boolean(patient?.do_not_email);
        const doNotMessage = Boolean(patient?.do_not_message);

        // Both do-not-contact flags set — keep notifications off
        if (doNotEmail && doNotMessage) {
          setValue("notification_method", null);
          setValue("should_notify", false);
          return;
        }

        let notification_method: number | null = null;

        if (doNotEmail && hasPhone) {
          // Email not allowed — default to SMS
          notification_method = NOTIFICATION_METHOD_SMS;
        } else if (doNotMessage && hasEmail) {
          // Text not allowed — default to Email
          notification_method = NOTIFICATION_METHOD_EMAIL;
        } else if (!doNotEmail && !doNotMessage) {
          if (
            hasPhone &&
            (Number(patient?.preferred_communication_method) ===
              COMMUNICATION_METHOD_TEXT_SMS ||
              !hasEmail)
          ) {
            notification_method = NOTIFICATION_METHOD_SMS;
          } else if (
            hasEmail &&
            (Number(patient?.preferred_communication_method) ===
              COMMUNICATION_METHOD_EMAIL ||
              !hasPhone)
          ) {
            notification_method = NOTIFICATION_METHOD_EMAIL;
          } else if (hasPhone && hasEmail) {
            notification_method = NOTIFICATION_METHOD_SMS;
          }
        }
        // doNotEmail && !hasPhone, or doNotMessage && !hasEmail: notification_method stays null

        setValue("notification_method", notification_method);
        setValue("should_notify", Boolean(shouldNotify && notification_method));
      } else {
        setValue("notification_method", NOTIFICATION_METHOD_SMS);
        setValue("should_notify", shouldNotify);
      }
    }
  }, [
    patient,
    setValue,
    isEmailNotificationEnabled,
    eventId,
    shouldNotify,
    isRescheduling
  ]);

  useEffect(() => {
    const staffClinic = availableStaffClinics[0] as Clinic;
    if (!clinic && (!multiClinic || isGlobalPracticeEvent)) {
      setValue("clinic", staffClinic || currentClinic || null, {
        shouldDirty: true,
        shouldValidate: true
      });
    }
  }, [
    clinic,
    currentClinic,
    setValue,
    multiClinic,
    isGlobalPracticeEvent,
    availableStaffClinics
  ]);

  useEffect(() => {
    if (overwriteBlock === undefined) {
      setValue(
        "overwrite_block",
        Boolean(
          defaultValues?.id &&
            appointmentType?.id === defaultValues?.appointment_type?.id
        )
      );
    }
  }, [setValue, appointmentType, defaultValues, overwriteBlock]);

  useEffect(() => {
    setValue("timezone", clinic?.timezone || undefined);
  }, [clinic?.timezone, setValue]);

  const shouldFetchTags = Boolean(
    isAssignedAppointmentTagsFeature && currentAppointment?.id
  );

  const {
    currentData: tags,
    isFetching: isTagsFetching,
    isLoading: isTagsLoading
  } = useGetAppointmentAssignedTagsQuery(
    {
      object_id: Number(currentAppointment?.id)
    },
    {
      skip: !shouldFetchTags || Number.isNaN(Number(currentAppointment?.id)),
      refetchOnMountOrArgChange: true
    }
  );

  useEffect(() => {
    if (isTagsInitialized || !(isOpen && openFormId === formId)) {
      return;
    }
    if (!isAssignedAppointmentTagsFeature) {
      setAssignedTags([]);
      setIsTagsInitialized(true);
    } else if (isReschedTransfer) {
      // Mirror the curated carried set (the save's source of truth), not the
      // source appointment's still-unchanged server tags. Re-seeds on every
      // open, so chips removed in the modal are reflected here too.
      setAssignedTags(carriedReschedulingTags);
      setIsTagsInitialized(true);
    } else if (currentAppointment?.id) {
      if (tags !== undefined || (!isTagsLoading && !isTagsFetching)) {
        setAssignedTags(tags || []);
        setIsTagsInitialized(true);
      }
    }
  }, [
    currentAppointment?.id,
    isTagsInitialized,
    isAssignedAppointmentTagsFeature,
    isReschedTransfer,
    carriedReschedulingTags,
    isTagsLoading,
    isTagsFetching,
    tags,
    isOpen,
    openFormId,
    formId
  ]);

  const handleTagDelete = useCallback(
    (tag_id: number) => {
      setAssignedTags((prev) => prev.filter((tag) => tag.id !== tag_id));
      // During a reschedule transfer the curated carried set is the save's
      // source of truth, so an in-form removal must curate it too — keeping the
      // form, the modal chips, and the saved payload in sync.
      if (isReschedTransfer) {
        dispatch(removeCarriedReschedulingTag(tag_id));
      }
    },
    [isReschedTransfer, dispatch]
  );

  const handleTagAdd = useCallback(
    (_tag_id: number, tag: Tag) => {
      setAssignedTags((prev) => [
        ...prev,
        {
          id: new Date().getTime(),
          tag,
          object_id: tag.id || 0,
          isDirty: true,
          created_by: user as any,
          updated_by: user as any,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }
      ]);
    },
    [user]
  );

  const handleTagUpdate = useCallback(
    (tag: AssignedTag) => {
      setAssignedTags((prev) => {
        const index = prev.findIndex((t) => t?.id === tag.id);
        if (index < 0) return prev;
        const newTags = [...prev];
        newTags[index] = {
          ...tag,
          isDirty: true,
          updated_by: user as any,
          updated_at: new Date().toISOString()
        };
        return newTags;
      });
    },
    [user]
  );

  const { patientCreateAppointmentMode } = usePatientCreateAppointmentMode();
  const { returnToPath } = useAppSelector(
    selectPatientCreateAppointmentModeValues
  );

  const navigate = useNavigate();
  const [recurrenceRuleEditorOpen, setRecurrenceRuleEditorOpen] =
    useState(false);

  useEffect(() => {
    setRecurrenceRuleEditorOpen(!Boolean(currentAppointment?.id));
  }, [currentAppointment?.id, formId]);

  const isRecurContainerOpened =
    recurringAppointmentFeatureEnabled &&
    is_recurring &&
    recurrenceRuleEditorOpen;

  const nonRRuleRecurrence = Boolean(
    currentAppointment &&
      !currentAppointment?.rrule &&
      !currentAppointment?.schedule?.rrule &&
      (currentAppointment.recurrence_day ||
        currentAppointment.recurrence_period ||
        currentAppointment.recurrence_week ||
        currentAppointment?.schedule?.recurrence_day ||
        currentAppointment?.schedule?.recurrence_period ||
        currentAppointment?.schedule?.recurrence_week)
  );

  return (
    <FormProvider {...formMethods}>
      <ModalForm
        key={eventType}
        title={
          originalAppointmentData
            ? getReschedulingTitle(
                originalAppointmentData,
                rescheduledAppointmentData
              )
            : `Schedule ${EVENT_TYPES.getFromCode(eventType)}`
        }
        supaDemoId={SUPADEMO_APPOINTMENT_FORM_DEMO_ID}
        submitIsDisabled={submitIsDisabled || isRecurContainerOpened}
        isSubmitting={isSubmitting}
        isOpen={isOpen && openFormId === formId}
        onCancel={() => {
          dispatch(resetConfirmDialog());
          reset();
          onCancel();
          dispatch(closeForm());
          // Clear follow-up scheduling state if active
          if (followUpScheduling.active) {
            dispatch(endFollowUpScheduling());
          }
        }}
        onSubmit={handleSubmit((data) => {
          setIsSubmitting(true);
          dispatch(resetConfirmDialog());
          const onSaveCallback = ({
            isSuccess
          }: {
            isSuccess: boolean;
            result: Appointment;
          }) => {
            setIsSubmitting(false);
            if (isSuccess) {
              dispatch(
                appointmentsApi.util.invalidateTags([
                  { type: "AssignedAppointmentTags" }
                ])
              );
              reset();
              if (
                isScheduleViewInPatientHeaderFeature &&
                patientCreateAppointmentMode
              ) {
                dispatch(endPatientCreateAppointmentMode());
                // Stay on scheduling when follow-up was started from scheduling page
                const stayOnScheduling =
                  returnToPath?.startsWith("/scheduling");
                if (!stayOnScheduling) {
                  navigate(`/patients/${patient.id}/appointments`);
                }
              }
              // Clear follow-up scheduling state after successful save
              if (followUpScheduling.active) {
                dispatch(endFollowUpScheduling());
              }
            }
          };

          const appointmentDate = new Date(data.appointment_date.toString());
          const newStartTime = convertDateFromCurrentTimezoneToTimezone(
            getDateTime(appointmentDate, data.start_time),
            data.timezone
          );
          const newEndTime = convertDateFromCurrentTimezoneToTimezone(
            getDateTime(appointmentDate, data.end_time),
            data.timezone
          );
          const removed_staff_members = currentAppointment.staff_members
            ? currentAppointment.staff_members.filter(
                (staff) =>
                  !data.staff_members.some(
                    (newStaff) => newStaff.id === staff.id
                  )
              )
            : [];

          const formattedData = {
            ...data,
            removed_staff_members,
            recurrence_period:
              data.recurrence_period ===
              REOCCURRENCE_PERIOD_TYPE_DAY_IN_WEEK_OF_MONTH
                ? REOCCURRENCE_PERIOD_TYPE_MONTHLY
                : data.recurrence_period,
            end_time: newEndTime,
            start_time: newStartTime
          };

          let appointment: Partial<Appointment> | Partial<Schedule>;
          if (data.event_type === EVENT_TYPE_APPOINTMENT) {
            appointment = {
              ...formatAppointment(formattedData),
              clinic: data.clinic || currentClinic || undefined,
              // Track follow-up appointment source
              ...(followUpScheduling.active &&
              followUpScheduling.completedAppointmentId
                ? {
                    created_from_appointment:
                      followUpScheduling.completedAppointmentId
                  }
                : {})
            };
          } else if (data.event_type === EVENT_TYPE_APPOINTMENT_BLOCK) {
            appointment = formatAppointmentBlock(
              formattedData,
              currentClinic as Clinic
            );
          } else {
            appointment = formatPersonalEvent({
              data: formattedData,
              clinic: currentClinic as Clinic,
              user,
              eventType: eventType as "Personal" | "Clinic" | "WorkShift"
            });
          }

          if (
            isAssignedAppointmentTagsFeature &&
            data.event_type === EVENT_TYPE_APPOINTMENT
          ) {
            const assigned_tags: AppointmentAssignedTagsInput = {};

            if (isReschedTransfer) {
              // Reschedule POSTs a NEW appointment, so the curated carried tags
              // are pure creates — no diff, no duplication, no omission. An
              // empty curated set carries nothing.
              const creates = buildReschedTransferCreates(
                carriedReschedulingTags
              );
              if (creates.length) {
                assigned_tags.create = creates;
              }
            } else {
              const { tagsToAdd, tagsToUpdate, tagsToDelete } =
                getAssignedTagChanges(
                  currentAppointment.id ? tags || [] : [],
                  assignedTags,
                  currentAppointment.id || -1
                );

              if (tagsToAdd.length) {
                assigned_tags.create = tagsToAdd.map((tag) => ({
                  tag: tag.tag.id,
                  description: tag.description,
                  expires_at: tag.expires_at
                    ? new Date(tag.expires_at).toISOString()
                    : undefined
                }));
              }

              if (tagsToUpdate.length) {
                assigned_tags.update = {};
                tagsToUpdate.forEach((tag) => {
                  (assigned_tags.update ??= {})[tag.id] = {
                    tag: tag.tag.id,
                    description: tag.description,
                    expires_at: tag.expires_at
                      ? new Date(tag.expires_at).toISOString()
                      : undefined
                  };
                });
              }

              if (tagsToDelete.length) {
                assigned_tags.remove = tagsToDelete.map((tag) => tag.id);
              }
            }
            (appointment as any).assigned_tags = assigned_tags;
          }

          return onSave(appointment as any).then(onSaveCallback);
        })}
        dialogContentSx={{
          p: 2
        }}
        dialogActionsSx={{
          position: "sticky",
          bottom: 10,
          right: 10,
          pb: 0,
          mb: 0,
          width: "min-content",
          marginLeft: "auto"
        }}
        maxWidth={
          eventType === EVENT_TYPE_APPOINTMENT && !isAppointmentInFinalState
            ? "xl"
            : "md"
        }
        formName="appointment"
        control={control}
        sx={{
          minWidth: [
            EVENT_TYPE_PERSONAL_EVENT,
            EVENT_TYPE_PERSONAL_WORKSHIFT,
            EVENT_TYPE_CLINIC_EVENT
          ].includes(eventType)
            ? 680
            : eventType === EVENT_TYPE_APPOINTMENT_BLOCK
              ? 770
              : undefined,
          minHeight:
            eventType === EVENT_TYPE_APPOINTMENT_BLOCK ? 560 : undefined,
          background: misc.lightMutedPurple
        }}
        submitButtonSx={{ width: 150 }}
        showBackground
      >
        <ShowPickersProvider>
          <Grid container>
            <Grid
              item
              xs={
                isMobile
                  ? 12
                  : eventType === EVENT_TYPE_APPOINTMENT &&
                      !isAppointmentInFinalState &&
                      schedulingStaffIsAvailability
                    ? 6
                    : 10
              }
            >
              <EventTypeField control={control} sx={{ mb: 2 }} />
              {eventType === EVENT_TYPE_APPOINTMENT && (
                <AppointmentFormView
                  defaultValues={defaultValues}
                  nonFieldErrors={errors}
                  appointmentId={currentAppointment?.id}
                  isAppointmentInFinalState={isAppointmentInFinalState}
                  isAppointmentConfirmed={
                    Boolean(eventId) && isAppointmentConfirmed
                  }
                  isPatientLocked={followUpScheduling.active}
                  multiClinic={multiClinic}
                  tags={assignedTags}
                  onTagDelete={handleTagDelete}
                  onTagAdd={handleTagAdd}
                  onTagUpdate={handleTagUpdate}
                  recurrenceRuleEditorOpen={recurrenceRuleEditorOpen}
                  setRecurrenceRuleEditorOpen={setRecurrenceRuleEditorOpen}
                  nonRRuleRecurrence={nonRRuleRecurrence}
                  isSubReferralSourceRequired={isSubReferralSourceRequired}
                />
              )}
              {(eventType === EVENT_TYPE_PERSONAL_EVENT ||
                eventType === EVENT_TYPE_PERSONAL_WORKSHIFT) && (
                <EventFormView
                  defaultValues={defaultValues}
                  nonFieldErrors={errors}
                  multiClinic={multiClinic}
                  isWorkShiftEvent={eventType === EVENT_TYPE_PERSONAL_WORKSHIFT}
                  showEventType={eventType === EVENT_TYPE_PERSONAL_EVENT}
                  recurrenceRuleEditorOpen={recurrenceRuleEditorOpen}
                  setRecurrenceRuleEditorOpen={setRecurrenceRuleEditorOpen}
                  appointmentId={
                    currentAppointment?.id || currentAppointment?.modelId
                  }
                  nonRRuleRecurrence={nonRRuleRecurrence}
                />
              )}
              {eventType === EVENT_TYPE_APPOINTMENT_BLOCK && (
                <AppointmentBlockFormView
                  defaultValues={defaultValues}
                  nonFieldErrors={errors}
                  multiClinic={multiClinic}
                  recurrenceRuleEditorOpen={recurrenceRuleEditorOpen}
                  setRecurrenceRuleEditorOpen={setRecurrenceRuleEditorOpen}
                  appointmentId={
                    currentAppointment?.id || currentAppointment?.modelId
                  }
                  nonRRuleRecurrence={nonRRuleRecurrence}
                />
              )}
              {isClinicEventsEnabled &&
                eventType === EVENT_TYPE_CLINIC_EVENT && (
                  <EventFormView
                    defaultValues={defaultValues}
                    nonFieldErrors={errors}
                    multiClinic={multiClinic}
                    isClinicEvent
                    showEventType
                    recurrenceRuleEditorOpen={recurrenceRuleEditorOpen}
                    setRecurrenceRuleEditorOpen={setRecurrenceRuleEditorOpen}
                    appointmentId={
                      currentAppointment?.id || currentAppointment?.modelId
                    }
                    nonRRuleRecurrence={nonRRuleRecurrence}
                  />
                )}
            </Grid>
            {eventType === EVENT_TYPE_APPOINTMENT &&
              !isAppointmentInFinalState &&
              schedulingStaffIsAvailability &&
              !isMobile && (
                <Grid item xs={6} pl={3}>
                  <StaffAvailability
                    control={control}
                    setValue={setValue}
                    visible={isOpen && openFormId === formId}
                    defaultShowFilter
                  />
                </Grid>
              )}
          </Grid>
        </ShowPickersProvider>
      </ModalForm>
    </FormProvider>
  );
}
